import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isValidYoutubeUrl } from '../utils/validation';
import { auth } from '../firebase';

function AddVideoModal({ isOpen, onClose, onSuccess }) {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [formData, setFormData] = useState({
        // Bilingual title fields
        title_en: '',
        title_tl: '',
        // Bilingual description fields
        description_en: '',
        description_tl: '',
        category: '',
        youtubeUrl: '',
        customThumbnail: '',
        thumbnailColor: 'linear-gradient(135deg, #c7252b, #e02d34)'
    });
    const [videoType, setVideoType] = useState('youtube'); // 'youtube' or 'upload'
    const [videoFile, setVideoFile] = useState(null);
    const [thumbnailFile, setThumbnailFile] = useState(null);

    if (!isOpen) return null;

    const categories = ['Promotional', 'Tutorials', 'Events', 'Other'];

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (error) setError('');
    };

    const handleFileChange = (e) => {
        if (e.target.files[0]) {
            setVideoFile(e.target.files[0]);
            if (error) setError('');
        }
    };

    const handleThumbnailChange = (e) => {
        if (e.target.files[0]) {
            setThumbnailFile(e.target.files[0]);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!formData.title_en || !formData.category) {
            setError(t('video_fill_required'));
            return;
        }

        if (videoType === 'youtube') {
            if (!formData.youtubeUrl) {
                setError(t('video_fill_required'));
                return;
            }
            if (!isValidYoutubeUrl(formData.youtubeUrl)) {
                setError(t('video_invalid_url'));
                return;
            }
        } else if (videoType === 'upload') {
            if (!videoFile) {
                setError(t('video_file_required') || 'Please select a video file');
                return;
            }
        }

        setLoading(true);

        try {
            const token = await auth.currentUser.getIdToken();
            const submitData = new FormData();

            // Append all fields
            submitData.append('title_en', formData.title_en);
            submitData.append('title_tl', formData.title_tl);
            submitData.append('description_en', formData.description_en);
            submitData.append('description_tl', formData.description_tl);
            submitData.append('category', formData.category);
            submitData.append('videoType', videoType);
            submitData.append('customThumbnail', formData.customThumbnail);
            submitData.append('thumbnailColor', formData.thumbnailColor);

            if (videoType === 'youtube') {
                submitData.append('youtubeUrl', formData.youtubeUrl);
            } else if (videoType === 'upload') {
                submitData.append('videoFile', videoFile);
            }

            if (thumbnailFile) {
                submitData.append('thumbnailFile', thumbnailFile);
            }

            const response = await fetch('/api/videos', {
                method: 'POST',
                headers: {
                    // Content-Type is set automatically for FormData
                    'Authorization': `Bearer ${token}`
                },
                body: submitData
            });

            if (!response.ok) {
                const data = await response.json();
                throw new Error(data.error || 'Failed to add video');
            }

            setFormData({
                title_en: '',
                title_tl: '',
                description_en: '',
                description_tl: '',
                category: '',
                youtubeUrl: '',
                customThumbnail: '',
                thumbnailColor: 'linear-gradient(135deg, #c7252b, #e02d34)'
            });
            setVideoFile(null);
            setThumbnailFile(null);
            setVideoType('youtube');

            if (onSuccess) onSuccess();
            onClose();
        } catch (err) {
            console.error('Error adding video:', err);
            setError(err.message || t('video_add_failed'));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content modal-large" onClick={e => e.stopPropagation()}>
                <button className="modal-close-btn" onClick={onClose}>×</button>
                <h3 className="modal-title">{t('video_add_modal_title')}</h3>
                <p className="modal-subtitle">{t('video_add_modal_subtitle')}</p>

                {error && <div className="error-banner">{error}</div>}

                <form className="modal-form" onSubmit={handleSubmit}>
                    {/* Bilingual Content Section */}
                    <div className="bilingual-section compact">
                        <h4 className="bilingual-section-title">
                            🌐 {t('bilingual_section_title')}
                        </h4>
                        <p className="bilingual-section-help" style={{ fontSize: '0.85rem' }}>{t('bilingual_section_help')}</p>

                        {/* English Content */}
                        <div className="language-block compact">
                            <div className="language-header">
                                <span className="language-flag">🇺🇸</span>
                                <span className="language-label">{t('english_content')}</span>
                            </div>

                            <div className="form-group">
                                <label>{t('video_title_en')} *</label>
                                <input
                                    type="text"
                                    name="title_en"
                                    className="form-input"
                                    value={formData.title_en}
                                    onChange={handleChange}
                                    placeholder={t('video_title_en_placeholder')}
                                    required
                                />
                            </div>

                            <div className="form-group">
                                <label>{t('video_desc_en')}</label>
                                <textarea
                                    name="description_en"
                                    className="form-input form-textarea"
                                    value={formData.description_en}
                                    onChange={handleChange}
                                    placeholder={t('video_desc_en_placeholder')}
                                    rows="2"
                                />
                            </div>
                        </div>

                        {/* Tagalog Content */}
                        <div className="language-block compact">
                            <div className="language-header">
                                <span className="language-flag">🇵🇭</span>
                                <span className="language-label">{t('tagalog_content')}</span>
                                <span className="optional-badge">{t('optional')}</span>
                            </div>

                            <div className="form-group">
                                <label>{t('video_title_tl')}</label>
                                <input
                                    type="text"
                                    name="title_tl"
                                    className="form-input"
                                    value={formData.title_tl}
                                    onChange={handleChange}
                                    placeholder={t('video_title_tl_placeholder')}
                                />
                            </div>

                            <div className="form-group">
                                <label>{t('video_desc_tl')}</label>
                                <textarea
                                    name="description_tl"
                                    className="form-input form-textarea"
                                    value={formData.description_tl}
                                    onChange={handleChange}
                                    placeholder={t('video_desc_tl_placeholder')}
                                    rows="2"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Category */}
                    <div className="form-group">
                        <label>{t('video_category')} *</label>
                        <select
                            name="category"
                            className="form-input"
                            value={formData.category}
                            onChange={handleChange}
                            required
                        >
                            <option value="">{t('video_select_category')}</option>
                            {categories.map(cat => (
                                <option key={cat} value={cat}>{cat}</option>
                            ))}
                        </select>
                    </div>

                    {/* Video Source Toggle */}
                    <div className="form-group">
                        <label>{t('video_source') || 'Video Source'}</label>
                        <div className="radio-group" style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                                <input
                                    type="radio"
                                    name="videoType"
                                    value="youtube"
                                    checked={videoType === 'youtube'}
                                    onChange={(e) => setVideoType(e.target.value)}
                                />
                                YouTube
                            </label>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                                <input
                                    type="radio"
                                    name="videoType"
                                    value="upload"
                                    checked={videoType === 'upload'}
                                    onChange={(e) => setVideoType(e.target.value)}
                                />
                                {t('video_upload') || 'Upload Video'}
                            </label>
                        </div>
                    </div>

                    {/* YouTube URL or File Upload */}
                    {videoType === 'youtube' ? (
                        <div className="form-group">
                            <label>{t('video_youtube_url')} *</label>
                            <input
                                type="url"
                                name="youtubeUrl"
                                className="form-input"
                                value={formData.youtubeUrl}
                                onChange={handleChange}
                                placeholder="https://www.youtube.com/watch?v=..."
                                required={videoType === 'youtube'}
                            />
                        </div>
                    ) : (
                        <div className="form-group">
                            <label>{t('video_file') || 'Video File'} *</label>
                            <input
                                type="file"
                                accept="video/*"
                                className="form-input"
                                onChange={handleFileChange}
                                required={videoType === 'upload'}
                            />
                            {videoFile && <small>{videoFile.name}</small>}
                        </div>
                    )}

                    {/* Custom Thumbnail URL */}
                    <div className="form-group">
                        <label htmlFor="thumbnailFile">Thumbnail (Optional)</label>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                            <input
                                type="url"
                                name="customThumbnail"
                                className="form-input"
                                value={formData.customThumbnail}
                                onChange={handleChange}
                                placeholder={t('video_custom_thumbnail_placeholder') || "Enter URL like https://example.com/image.jpg"}
                            />
                            <div style={{ textAlign: 'center', fontSize: '0.8rem', opacity: 0.7 }}>- OR -</div>
                            <input
                                id="thumbnailFile"
                                type="file"
                                accept="image/*"
                                className="form-input"
                                onChange={handleThumbnailChange}
                            />
                            {thumbnailFile && <small>Selected: {thumbnailFile.name}</small>}
                        </div>
                        <small className="form-help-text">{t('video_custom_thumbnail_help')}</small>
                    </div>

                    {/* Card Gradient */}
                    <div className="form-group">
                        <label>{t('video_card_gradient')}</label>
                        <select
                            name="thumbnailColor"
                            className="form-input"
                            value={formData.thumbnailColor}
                            onChange={handleChange}
                        >
                            <option value="linear-gradient(135deg, #c7252b, #e02d34)">Red (Default)</option>
                            <option value="linear-gradient(135deg, #f6cc25, #e6a821)">Gold</option>
                            <option value="linear-gradient(135deg, #1f4282, #00225e)">Blue</option>
                            <option value="linear-gradient(135deg, #4CAF50, #2E7D32)">Green</option>
                            <option value="linear-gradient(135deg, #9C27B0, #673AB7)">Purple</option>
                        </select>
                    </div>

                    <div className="modal-actions">
                        <button
                            type="button"
                            className="btn-outline-modern"
                            onClick={onClose}
                            disabled={loading}
                        >
                            {t('cancel')}
                        </button>
                        <button
                            type="submit"
                            className="btn-primary-modern"
                            disabled={loading}
                        >
                            {loading ? t('video_adding') : t('video_add_submit')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default AddVideoModal;
